import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/ui/docs-site/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
/*-- dri: Lynette Miles --*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Create a Slack notifier";
const frontMatter = {
  "title": "Create a Slack notifier",
  "description": "Create a Slack notifier with Chronosphere Observability Platform, Chronoctl, or Terraform."
};
import {Icon, Tabs} from '@/components';
import {mdiBell} from '@mdi/js';
import ApiAuthReq, {useTOC as useTOC0} from '@/_partials/api-auth-req.mdx';
import ChronoctlCreate, {useTOC as useTOC1} from '@/_partials/chronoctlnotifiers.mdx';
import TerraformCreate, {useTOC as useTOC2} from '@/_partials/terraformcreate.mdx';
export function useTOC(props) {
  return [{
    value: "Create an incoming webhook in Slack",
    id: "create-an-incoming-webhook-in-slack",
    depth: 2
  }, {
    value: "Create a Slack notifier in Observability Platform",
    id: "create-a-slack-notifier-in-observability-platform",
    depth: 2
  }, ...useTOC1(), ...useTOC0()];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h1: "h1",
    h2: "h2",
    li: "li",
    mark: "mark",
    ol: "ol",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  if (!Tabs) _missingMdxReference("Tabs", false);
  if (!Tabs.Tab) _missingMdxReference("Tabs.Tab", true);
  return <>{}{"\n"}<_components.h1>{"Create a Slack notifier"}</_components.h1>{"\n"}<_components.p>{"To create a Slack notifier, generate an incoming webhook in Slack and then create a\nSlack notifier in Chronosphere Observability Platform."}</_components.p>{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<_components.p>{"Before creating a Slack notifier in Observability Platform, create an incoming webhook in Slack. See\n"}<_components.a href="https://api.slack.com/messaging/webhooks">{"Sending messages using incoming webhooks"}</_components.a>{"\nin the Slack documentation for more information."}</_components.p>{"\n"}<_components.h2 id={toc[1].id}>{toc[1].value}</_components.h2>{"\n"}<_components.p>{"After "}<_components.a href="#create-an-incoming-webhook-in-slack">{"creating an incoming webhook in Slack"}</_components.a>{",\nyou can create a Slack notifier in Observability Platform. You can use\n"}<_components.a href="/investigate/alerts/notifications/notifiers#use-variables-in-notifiers">{"variables"}</_components.a>{" in your\nnotifiers."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="create-slack-notifier"><_components.p>{"To create a Slack notifier:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"In the navigation menu select\n"}<_components.strong><Icon path={mdiBell} />{" Alerts "}<span aria-label="and then">{">"}</span>{" Notifiers"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Create notifier"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter a descriptive name for the notifier."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Select "}<_components.strong>{"Slack"}</_components.strong>{" as the type of notifier you want to create."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the "}<_components.strong>{"API URL"}</_components.strong>{" field, enter the URL you created for your webhook in Slack,\nwhich is called as a "}<_components.code>{"POST"}</_components.code>{" request. For example:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="text" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span>{"https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXX"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the "}<_components.strong>{"Channel"}</_components.strong>{" field, enter the Slack channel you want the notifier\nto post to."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Optional: In the "}<_components.strong>{"Username"}</_components.strong>{" field, enter the Slack username to post the notifier\nas."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Optional: Select "}<_components.strong>{"Notify when resolved"}</_components.strong>{" to send a resolved alert notification."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Save"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="slack-chronoctl"><ChronoctlCreate /><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Define the resource definition for your webhook notifier:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"api_version"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" v1/config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"kind"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Notifier"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"spec"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"NAME"}</_components.span></_components.mark></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"SLUG"}</_components.span></_components.mark></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  skip_resolved"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" true"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  slack"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" actions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#ED8796",
    "--shiki-light": "#1976D2"
  }}>{" null"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      api_url"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" https://hooks.slack.com/services/"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      channel"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"CHANNEL"}</_components.span></_components.mark></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      fields"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#ED8796",
    "--shiki-light": "#1976D2"
  }}>{" null"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      mrkdwn_in"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#ED8796",
    "--shiki-light": "#1976D2"
  }}>{" null"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      username"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"USER"}</_components.span></_components.mark><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"NAME"}</_components.span></_components.mark></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Replace the following:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.em><_components.code>{"NAME"}</_components.code></_components.em>{": A descriptive name, such as "}<_components.code>{"test-slack"}</_components.code>{"."}</_components.li>{"\n"}<_components.li><_components.em><_components.code>{"SLUG"}</_components.code></_components.em>{": A unique identifier, such as "}<_components.code>{"team-slack"}</_components.code>{"."}</_components.li>{"\n"}<_components.li><_components.em><_components.code>{"CHANNEL"}</_components.code></_components.em>{": The Slack channel to send notifications to."}</_components.li>{"\n"}<_components.li><_components.em><_components.code>{"USERNAME"}</_components.code></_components.em>{": Send the notification message from a specified Slack username."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter the URL you created for your webhook in Slack as the\nvalue for the "}<_components.code>{"api_url"}</_components.code>{" field."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter the Slack channel you want the notifier to post to in the "}<_components.code>{"channel"}</_components.code>{" field."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Optional: Enter the Slack username to post the notifier as in the "}<_components.code>{"username"}</_components.code>{"\nfield."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Apply the changes:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -f"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"FILE_NAME"}</_components.span></_components.mark><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{".yaml"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Replace "}<_components.em><_components.code>{"FILE_NAME"}</_components.code></_components.em>{" with the name of your notifier YAML file."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Terraform" id="slack-terraform"><_components.p>{"To create a Slack notifier:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Create a Slack notifier with Terraform by using the\n"}<_components.code>{"chronosphere_slack_alert_notifier"}</_components.code>{" type followed by a name in a resource\ndeclaration:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"resource "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_slack_alert_notifier\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"slack_notifier\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Slack Notifier\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Optional slug of the slack notifier."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  slug = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"slack-notifier\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  ## Notifier-specific required configuration."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # For detailed definitions, see"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # https://prometheus.io/docs/alerting/latest/configuration/#slack_config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # `api_url` is a sensitive value. Treat it like a secret."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  api_url       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"https://hooks.slack.com/services/XXXXX/XXXXX/XXXXX\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  channel       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"alerts\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  ## Notifier-specific optional configurations"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  username      = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"alertbot\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  callback_id   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_callback_id\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  color         = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"red\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  fallback      = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_fallback\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  footer        = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_footer\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  icon_emoji    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  image_url     = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"https://example.com/images/img.png\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  link_names    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"false"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  mrkdwn_in     = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"[]"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  pretext       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_pretext\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  short_fields  = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"false"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  text          = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_text\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  title         = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_title\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  title_link    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_title_link\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Can have multiple fields."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  fields "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    title = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_title\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_value\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    short = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"true"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Optional list of actions"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  action "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    name                        = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_action\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    style                       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_style\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    text                        = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_text\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    type                        = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_type\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    url                         = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_url\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    value                       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"my_value\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    action_confirm_text         = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"confirmation text\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    action_confirm_tile         = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"confirmation title\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    action_confirm_ok_text      = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"confirmation ok text\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    action_confirm_dismiss_text = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"dismissal text\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  ## (Optional) Base configuration common to all notifiers"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  send_resolved = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"true"}</_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{" # The default value"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  ## (Optional) HTTP configuration common to HTTP notifiers"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  basic_auth_username = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"username\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  basic_auth_password = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"strong+p@ssword\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Can use bearer_token instead of basic auth"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  proxy_url                = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"https://my-proxy-url\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  tls_insecure_skip_verify = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"false"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter the URL you created for your webhook in Slack as the\nvalue for the "}<_components.code>{"api_url"}</_components.code>{" field."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter the Slack channel you want the notifier to post to in the "}<_components.code>{"channel"}</_components.code>{" field."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Optional: Enter the Slack username to post the notifier as in the "}<_components.code>{"username"}</_components.code>{"\nfield."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run "}<_components.code>{"terraform apply"}</_components.code>{" to create the notifier resource."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"terraform apply"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="API" id="slack-API"><_components.p>{"To complete this action with the Chronosphere API, use the "}<_components.code>{"slack"}</_components.code>{" object in the\n"}<_components.a href="/tooling/api-info/definition/operations/CreateNotifier"><_components.code>{"CreateNotifier"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs></>;
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/investigate/alerts/notifications/notifiers/slack',
  {"filePath":"pages/investigate/alerts/notifications/notifiers/slack.mdx",pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)